import { useQueryClient, useMutation, useQuery } from 'react-query';

import {
  ActionState,
  ActionType,
  MutationType,
  SurveyCountResponseType,
  SurveysResponseType,
  SurveyType,
  TemplatesGalleryResponseType,
  TemplatesGalleryType,
} from 'shared/types';
import request from 'utils/request';

export const useGetTemplateById = ({
  templateId,
  callback,
}: MutationType & {
  templateId: string;
}) => {
  return useQuery(
    ['templates', templateId],
    async () => {
      if (!templateId) return;
      const data: SurveyType = await request(`templates/${templateId}/`);
      return data;
    },
    {
      enabled: Boolean(templateId),
      onSuccess: (response) => {
        if (callback) callback(true, response);
      },
      onError: (error) => {
        if (callback) callback(false, error);
      },
    },
  );
};

export const useTemplatesGallery = ({ callback }: MutationType) => {
  return useQuery(
    ['templates-gallery'],
    async () => {
      const data: TemplatesGalleryResponseType = await request(
        'templates/gallery',
      );

      const response: TemplatesGalleryType[] = (data?.templates || []).map(
        (template: any) => ({
          ...template,
          survey: {
            ...template.survey,
            ui_theme: {
              primary_background_color: '#ebeff3',
              secondary_button_text_color: '#111',
              primary_button_text_color: '#fff',
              question_background_color: '#fff',
              question_text_color: '#12344d',
              ...template.survey.ui_theme,
            },
            state: 'DRAFT',
            question_details: {
              ...template.survey.question_details,
              questions: template.survey.question_details.questions.map(
                // temp fix till survey preview component adds default value
                (question: any) =>
                  question.type_info.question_type === 'RANGE'
                    ? {
                        ...question,
                        type_info: {
                          ...question.type_info,
                          linear_scale: {
                            button_style: 'highlighted',
                            button_shape: 'rounded',
                          },
                          validation: {
                            min: 0,
                            max: 10,
                          },
                          rating_presets: {
                            start: 'Very unlikely',
                            end: 'Very likely',
                          },
                        },
                      }
                    : question,
              ),
            },
          },
        }),
      );
      return response;
    },
    {
      onSuccess: (response: TemplatesGalleryType[]) => {
        if (callback) callback(true, response);
      },
    },
  );
};

export const useSavedTemplates = () => {
  return useQuery(
    ['templates'],
    async () => {
      const data: SurveysResponseType = await request('templates');
      return data.surveys;
    },
    {
      refetchOnMount: 'always',
    },
  );
};

export type SaveTemplateType = ActionType &
  ActionState & {
    payload: SurveyType;
    id: string;
    isSaveAsNewTemplate?: boolean;
  };

export const useSaveTemplate = ({ setToast, callback }: MutationType) => {
  const queryClient = useQueryClient();
  return useMutation<SurveyType, any, SaveTemplateType>(
    async ({ payload, id }) => {
      const data: SurveyType = await request(
        id ? `templates/${id}/` : `templates/`,
        payload,
        id ? 'PUT' : 'POST',
      );
      return data;
    },
    {
      onSuccess: (response: SurveyType, variables) => {
        if (variables.id) {
          queryClient.setQueryData(['templates', `${response.id}`], response);
        } else {
          queryClient.refetchQueries(['templates']);
        }
        if (setToast)
          setToast({
            type: 'success',
            delay: 3000,
            text: variables.id
              ? 'Template saved successfully'
              : 'Template created successfully',
          });
        if (callback) callback(true, response, variables);
      },
      onError: (err: any, variables) => {
        if (callback) callback(false, err, variables);
      },
    },
  );
};

export const useDeleteTemplate = ({ setToast, callback }: MutationType) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (templateId: string) => {
      if (!templateId) return;
      const data = await request(`templates/${templateId}`, {}, 'DELETE');
      return data;
    },
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries('templates');
        queryClient.invalidateQueries('templates-count');
        if (setToast)
          setToast({
            type: 'success',
            delay: 3000,
            text: 'Template deleted',
          });
        if (callback) callback(true, response);
      },
      onError: (error) => {
        if (setToast)
          setToast({
            type: 'error',
            delay: 3000,
            text: 'Template deletion failed',
          });
        if (callback) callback(false, error);
      },
    },
  );
};

export const useGetAllTemplatesCount = ({
  callback,
  enabled = true,
  refetchOnWindowFocus = false,
}: MutationType) => {
  return useQuery<number>(
    ['templates-count'],
    async () => {
      const data: SurveyCountResponseType = await request(
        `surveys/count?is_template=true`,
      );
      return data.count ? Number(data.count || '0') : NaN;
    },
    {
      enabled,
      refetchOnWindowFocus,
      staleTime: refetchOnWindowFocus ? 10000 : 300000,
      onSuccess: (response: number) => {
        if (callback) callback(true, response);
      },
      onError: (err) => {
        if (callback) callback(false, err);
      },
    },
  );
};
